import apiService, { SLOW_API_TIMEOUT_MS } from '../api'

const financeOverviewConfig = { timeout: SLOW_API_TIMEOUT_MS }

export interface InvoiceRecord {
  id: string
  number: string
  ref: string | null
  document: string | null
  date: string | null
  dueDate: string | null
  paid: number
  due: number
  status: string
  xeroInvoiceId?: string | null
  createdAt: string
  updatedAt: string
}

export interface PurchaseRecord {
  id: string
  from: string
  invoiceNo: string | null
  invoiceDate: string | null
  invoiceDueDate: string | null
  paid: number
  due: number
  currency: string
  status: string
  externalUrl?: string | null
  createdAt: string
  updatedAt: string
}

export interface ProductServiceRecord {
  id: string
  code: string
  name: string
  costPrice: number
  xeroItemId?: string | null
  createdAt: string
  updatedAt: string
}

export interface PaginationMeta {
  page: number
  limit: number
  total: number
  totalPages: number
}

export interface InvoicesListResponse {
  invoices: InvoiceRecord[]
  meta: PaginationMeta
  totalBalanceDue?: number
}

export interface PurchasesListResponse {
  purchases: PurchaseRecord[]
  meta: PaginationMeta
}

export interface ProductsListResponse {
  products: ProductServiceRecord[]
  meta: PaginationMeta
}

export interface FinanceMetricTrends {
  totalRevenue: number[]
  pendingPayments: number[]
  paidInvoices: number[]
  overdueAmount: number[]
}

export interface FinanceOverviewResponse {
  totalRevenue: number
  pendingPayments: number
  paidInvoices: number
  overdueAmount: number
  profitLoss: { month: string; loss: number; profit: number }[]
  netIncome: { month: string; current: number; prior: number }[]
  clientRevenue: { name: string; value: number }[]
  metricTrends?: FinanceMetricTrends
}

export interface CashFlowManagerData {
  asOfDate: string
  summaryNarrative: string
  cards: {
    receivableNet: number
    receivableGross: number
    overdueReceivable: number
    payableZarNet: number
    payableZarGross: number
    overduePayableZar: number
    payableUsd: number
    overduePayableUsd: number
    netPositionZar: number
    setOffAmount: number
    setOffLabel: string
  }
  buckets: Array<{
    key: string
    label: string
    receipts: number
    payments: number
    net: number
    cumulative: number
  }>
  setOff: {
    title: string
    note: string
    items: Array<{ item: string; amount: number }>
    netBalance: number
  } | null
  dualCounterparties: Array<{
    counterparty: string
    theyOweUs: number
    weOweThem: number
    netInKcgFavour: number
    status: string
    setOffApplied: boolean
    setOffAmount: number
  }>
  receivableByCustomer: Array<{
    counterparty: string
    currency: string
    overdue: number
    notYetDue: number
    total: number
  }>
  payableBySupplier: Array<{
    counterparty: string
    currency: string
    overdue: number
    notYetDue: number
    total: number
  }>
  source: string
}

export async function getCashFlowManager(asOf?: string): Promise<CashFlowManagerData> {
  const qs = asOf ? `?asOf=${encodeURIComponent(asOf)}` : ''
  const res = await apiService.get<{ success: boolean; data: CashFlowManagerData }>(
    `/finance/cash-flow${qs}`,
    financeOverviewConfig
  )
  const data = (res as any)?.data ?? res
  return data as CashFlowManagerData
}

export async function getFinanceOverview(params?: {
  startDate?: string
  endDate?: string
  timeframe?: string
}): Promise<FinanceOverviewResponse> {
  const qs = params ? new URLSearchParams(params as Record<string, string>).toString() : ''
  const res = await apiService.get<{ success: boolean; data: FinanceOverviewResponse }>(
    `/finance/overview${qs ? '?' + qs : ''}`,
    financeOverviewConfig
  )
  const data = (res as any)?.data ?? res
  return {
    totalRevenue: data.totalRevenue ?? 0,
    pendingPayments: data.pendingPayments ?? 0,
    paidInvoices: data.paidInvoices ?? 0,
    overdueAmount: data.overdueAmount ?? 0,
    profitLoss: data.profitLoss ?? [],
    netIncome: data.netIncome ?? [],
    clientRevenue: data.clientRevenue ?? [],
    metricTrends: data.metricTrends,
  }
}

function formatDate(d: string | Date | null): string {
  if (!d) return '—'
  const date = typeof d === 'string' ? new Date(d) : d
  if (isNaN(date.getTime())) return '—'
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

function formatCurrency(amount: number, currency = 'ZAR'): string {
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency,
    currencyDisplay: 'code',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount)
}

function capitalizeStatus(s: string): string {
  if (!s) return '—'
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()
}

export async function getInvoiceCustomers(): Promise<{ customers: string[] }> {
  const res = await apiService.get<{ success: boolean; data: { customers: string[] } }>('/finance/invoices/customers')
  const data = (res as any)?.data ?? res
  return { customers: data.customers ?? [] }
}

export async function getInvoices(params?: {
  page?: number
  limit?: number
  search?: string
  status?: string
  statementType?: string
  customer?: string
  startDate?: string
  endDate?: string
  asAtDate?: string
}): Promise<InvoicesListResponse> {
  const filtered = params
    ? Object.fromEntries(
        Object.entries(params).filter(([, v]) => v != null && v !== '' && String(v) !== 'undefined')
      )
    : {}
  const qs = Object.keys(filtered).length ? new URLSearchParams(filtered as Record<string, string>).toString() : ''
  const res = await apiService.get<{ success: boolean; data: InvoicesListResponse }>(
    `/finance/invoices${qs ? '?' + qs : ''}`
  )
  const data = (res as any)?.data ?? res
  return {
    invoices: data.invoices ?? [],
    meta: data.meta ?? { page: 1, limit: 10, total: 0, totalPages: 0 },
    totalBalanceDue: data.totalBalanceDue ?? 0,
  }
}

export interface PurchasesSummaryResponse {
  totalPurchases: number
  pendingPayments: number
  paidPurchases: number
  unpaidPurchases: number
}

export async function getPurchasesSummary(): Promise<PurchasesSummaryResponse> {
  const res = await apiService.get<{ success: boolean; data: PurchasesSummaryResponse }>('/finance/purchases/summary')
  const data = (res as any)?.data ?? res
  return {
    totalPurchases: data.totalPurchases ?? 0,
    pendingPayments: data.pendingPayments ?? 0,
    paidPurchases: data.paidPurchases ?? 0,
    unpaidPurchases: data.unpaidPurchases ?? 0,
  }
}

export async function getPurchases(params?: {
  page?: number
  limit?: number
  search?: string
  status?: string
  startDate?: string
  endDate?: string
}): Promise<PurchasesListResponse> {
  const qs = params ? new URLSearchParams(params as Record<string, string>).toString() : ''
  const res = await apiService.get<{ success: boolean; data: PurchasesListResponse }>(
    `/finance/purchases${qs ? '?' + qs : ''}`
  )
  const data = (res as any)?.data ?? res
  return { purchases: data.purchases ?? [], meta: data.meta ?? { page: 1, limit: 10, total: 0, totalPages: 0 } }
}

/** Fetch purchase bill PDF from Xero and return a blob URL for viewing (opens in new tab). */
export async function getPurchaseBillPdfUrl(purchaseId: string): Promise<string> {
  const response = await apiService.instance.get(`/finance/purchases/${purchaseId}/bill-pdf`, {
    responseType: 'blob',
  })
  const blob = response.data as Blob
  // Ensure blob has correct MIME type for PDF
  const pdfBlob = blob.type === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' })
  return URL.createObjectURL(pdfBlob)
}

export async function getProductsServices(params?: {
  page?: number
  limit?: number
  search?: string
}): Promise<ProductsListResponse> {
  const query: Record<string, string> = {}
  if (params?.page != null) query.page = String(params.page)
  if (params?.limit != null) query.limit = String(params.limit)
  if (params?.search?.trim()) query.search = params.search.trim()
  const qs = Object.keys(query).length ? new URLSearchParams(query).toString() : ''
  const res = await apiService.get<{ success: boolean; data: ProductsListResponse; meta?: any }>(
    `/finance/products-services${qs ? '?' + qs : ''}`
  )
  // Backend returns { success, data: { products, meta }, meta }
  const responseData = res.data || res
  return { 
    products: responseData.products ?? [], 
    meta: responseData.meta ?? { page: 1, limit: 10, total: 0, totalPages: 0 } 
  }
}

/** Format amount as Xero-style: 818,000.00 (comma thousands, 2 decimals) */
function formatAmount(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount)
}

/** Map API invoice to table row (Xero Sales Overview: Date, Activity, Reference, Due Date, Invoice Amount, Payments, Balance ZAR) */
export function invoiceToTableRow(inv: InvoiceRecord) {
  const paid = Number(inv.paid) ?? 0
  const due = Number(inv.due) ?? 0
  const total = paid + due
  return {
    id: inv.id,
    document: inv.document ?? '—',
    number: inv.number,
    ref: inv.ref ?? '—',
    date: formatDate(inv.date),
    dueDate: formatDate(inv.dueDate),
    invoiceAmount: formatAmount(total),
    payments: formatAmount(paid),
    balanceZar: formatAmount(due),
    paid: formatAmount(paid),
    due: formatAmount(due),
    status: capitalizeStatus(inv.status),
    xeroInvoiceId: inv.xeroInvoiceId ?? null,
    _raw: inv,
  }
}

/** Map API purchase to table row (Xero column order: Contact, Reference, Date, Due date, Total, Balance due, Currency, Status) */
export function purchaseToTableRow(p: PurchaseRecord) {
  const paid = Number(p.paid) ?? 0
  const due = Number(p.due) ?? 0
  return {
    id: p.id,
    from: p.from,
    invoiceNo: p.invoiceNo ?? '—',
    invoiceDate: formatDate(p.invoiceDate),
    invoiceDueDate: formatDate(p.invoiceDueDate),
    paid: formatCurrency(paid, p.currency),
    due: formatCurrency(due, p.currency),
    currency: p.currency,
    status: capitalizeStatus(p.status),
    fileUrl: p.externalUrl ?? null,
    _raw: p,
  }
}

/** Map API product to table row (Xero column order: Code, Name, Description, Purchase, Sales, Quantity on hand) */
export function productToTableRow(p: ProductServiceRecord) {
  const cost = Number(p.costPrice) ?? 0
  return {
    id: p.id,
    code: p.code,
    name: p.name,
    costPrice: formatCurrency(cost),
    salePrice: '—',
    quantity: '0',
    xeroItemId: p.xeroItemId ?? null,
    _raw: p,
  }
}
